/**
 * MetabaseRoutes Component
 * 
 * Configures routing for the Metabase admin interface.
 * Provides three routes:
 * - "/" - List view (MetabaseList)
 * - "/new" - Create new report (MetabaseDetail in create mode)
 * - "/:id" - Edit existing report (MetabaseDetail in edit mode)
 * 
 * Uses lazy loading for MetabaseDetail to reduce initial bundle size.
 * Wraps lazy-loaded components in Suspense with loading fallback.
 * 
 * Requirements: 4.1, 5.1, 13.1, 13.2, 13.3, 13.4
 */

import { Routes, Route } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { Box, CircularProgress } from '@mui/material';

import MetabaseReportingPlaceholder from '../metabase/MetabaseReportingPlaceholder';

// Lazy load MetabaseDetail to reduce initial bundle size
const MetabaseDetail = lazy(() => import('../metabase/MetabaseDetail'));

/**
 * Loading fallback component for Suspense
 * Displays a centered loading spinner while the lazy component loads
 */
const LoadingFallback = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="200px"
  >
    <CircularProgress />
  </Box>
);

/**
 * MetabaseRoutes Component
 * 
 * Defines the routing structure for the Metabase admin interface.
 * Uses React Router v6 patterns with nested routes.
 */
export default function MetabaseRoutes() {
  return (
    <Routes>
      {/* Primary View - Shows Metabase placeholder dashboard & reports */}
      <Route index element={<MetabaseReportingPlaceholder />} />
      
      {/* Create View - New report form */}
      <Route 
        path="new" 
        element={
          <Suspense fallback={<LoadingFallback />}>
            <MetabaseDetail />
          </Suspense>
        } 
      />
      
      {/* Edit View - Existing report form */}
      <Route 
        path=":id" 
        element={
          <Suspense fallback={<LoadingFallback />}>
            <MetabaseDetail />
          </Suspense>
        } 
      />
    </Routes>
  );
}